import React, { useState, useEffect } from 'react';
import { CheckCircle, Target, TrendingUp, Star, ArrowRight, Mail, Calendar, BarChart3, Users, Lock } from 'lucide-react';

const BACKEND_URL = window.location.hostname === 'sierrascala.com' || window.location.hostname === 'www.sierrascala.com'
  ? ''
  : process.env.REACT_APP_BACKEND_URL;

const SECTION_ICONS = {
  hero: null,
  audit: Target,
  strategy: TrendingUp,
  stats: BarChart3,
  bullets: CheckCircle,
  cta: Mail,
  custom: Star,
};

const HeroSection = ({ section, template }) => (
  <section className="relative py-20 overflow-hidden" style={{ background: `linear-gradient(135deg, ${template.accent_color} 0%, ${template.accent_color}ee 100%)` }}>
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-10 left-10 w-64 h-64 rounded-full" style={{ background: template.primary_color, filter: 'blur(80px)' }} />
      <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full" style={{ background: template.primary_color, filter: 'blur(100px)' }} />
    </div>
    <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
      <p className="text-sm font-semibold tracking-widest uppercase mb-4" style={{ color: template.primary_color }}>
        Prepared for {template.prepared_for || template.company_name}
      </p>
      <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight">{section.title}</h1>
      {section.subtitle && <p className="text-lg text-slate-300 max-w-2xl mx-auto">{section.subtitle}</p>}
      <div className="mt-8 flex items-center justify-center gap-3">
        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
          <span className="text-white text-sm font-bold">SS</span>
        </div>
        <div className="text-left">
          <p className="text-white text-sm font-semibold">Sierra Scala</p>
          <p className="text-slate-400 text-xs">Digital Marketing Strategist</p>
        </div>
      </div>
    </div>
  </section>
);

const AuditSection = ({ section, template }) => (
  <section className="py-16 bg-white">
    <div className="max-w-4xl mx-auto px-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${template.primary_color}15` }}>
          <Target size={20} style={{ color: template.primary_color }} />
        </div>
        <h2 className="text-2xl font-bold text-slate-800">{section.title}</h2>
      </div>
      {section.content && <p className="text-slate-600 mb-6">{section.content}</p>}
      {section.bullets && section.bullets.length > 0 && (
        <div className="space-y-3">
          {section.bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
              <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${template.primary_color}20` }}>
                <span className="text-xs font-bold" style={{ color: template.primary_color }}>{i + 1}</span>
              </div>
              <p className="text-slate-700 text-sm">{b}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  </section>
);

const StrategySection = ({ section, template }) => (
  <section className="py-16" style={{ background: `${template.accent_color}08` }}>
    <div className="max-w-4xl mx-auto px-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${template.primary_color}15` }}>
          <TrendingUp size={20} style={{ color: template.primary_color }} />
        </div>
        <h2 className="text-2xl font-bold text-slate-800">{section.title}</h2>
      </div>
      {section.subtitle && <p className="font-medium text-slate-700 mb-4">{section.subtitle}</p>}
      {section.content && <p className="text-slate-600 leading-relaxed">{section.content}</p>}
      {section.bullets && section.bullets.length > 0 && (
        <div className="mt-6 grid gap-3">
          {section.bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-2">
              <CheckCircle size={16} className="mt-0.5 flex-shrink-0" style={{ color: template.primary_color }} />
              <span className="text-slate-700 text-sm">{b}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  </section>
);

const StatsSection = ({ section, template }) => (
  <section className="py-16 bg-white">
    <div className="max-w-4xl mx-auto px-6">
      {section.title && (
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${template.primary_color}15` }}>
            <BarChart3 size={20} style={{ color: template.primary_color }} />
          </div>
          <h2 className="text-2xl font-bold text-slate-800">{section.title}</h2>
        </div>
      )}
      {section.content && <p className="text-slate-600 mb-8">{section.content}</p>}
      {section.stats && section.stats.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {section.stats.map((s, i) => (
            <div key={i} className="text-center p-6 rounded-2xl border border-slate-100 bg-slate-50/50">
              <p className="text-3xl font-bold mb-1" style={{ color: template.primary_color }}>{s.metric}</p>
              <p className="text-sm text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  </section>
);

const BulletsSection = ({ section, template }) => (
  <section className="py-16" style={{ background: `${template.accent_color}05` }}>
    <div className="max-w-4xl mx-auto px-6">
      {section.title && <h2 className="text-2xl font-bold text-slate-800 mb-6">{section.title}</h2>}
      {section.content && <p className="text-slate-600 mb-6">{section.content}</p>}
      {section.bullets && (
        <div className="space-y-3">
          {section.bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-3">
              <CheckCircle size={18} className="mt-0.5 flex-shrink-0" style={{ color: template.primary_color }} />
              <span className="text-slate-700">{b}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  </section>
);

const CtaSection = ({ section, template }) => (
  <section className="py-20" style={{ background: template.accent_color }}>
    <div className="max-w-3xl mx-auto px-6 text-center">
      <h2 className="text-3xl font-bold text-white mb-4">{section.title}</h2>
      {section.content && <p className="text-slate-300 mb-8 text-lg">{section.content}</p>}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <a href="mailto:me@sierrascala.com" className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold text-white transition-all hover:shadow-lg" style={{ background: template.primary_color }}>
          <Mail size={18} /> me@sierrascala.com
        </a>
        <a href="https://calendly.com/sierra-scala123/30min" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full font-semibold transition-all">
          <Calendar size={18} /> Schedule a Call
        </a>
      </div>
    </div>
  </section>
);

const CustomSection = ({ section, template }) => (
  <section className="py-16 bg-white">
    <div className="max-w-4xl mx-auto px-6">
      {section.title && <h2 className="text-2xl font-bold text-slate-800 mb-4">{section.title}</h2>}
      {section.subtitle && <p className="text-lg text-slate-600 mb-4">{section.subtitle}</p>}
      {section.content && <div className="text-slate-700 leading-relaxed whitespace-pre-line">{section.content}</div>}
      {section.bullets && (
        <ul className="mt-4 space-y-2">
          {section.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2">
              <Star size={14} className="mt-1 flex-shrink-0" style={{ color: template.primary_color }} />
              <span className="text-slate-700 text-sm">{b}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  </section>
);

const SECTION_RENDERERS = {
  hero: HeroSection,
  audit: AuditSection,
  strategy: StrategySection,
  stats: StatsSection,
  bullets: BulletsSection,
  cta: CtaSection,
  custom: CustomSection,
};

const DynamicProposalPage = ({ slug }) => {
  const [meta, setMeta] = useState(null);
  const [template, setTemplate] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [requestName, setRequestName] = useState('');
  const [requestEmail, setRequestEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem(`proposal_${slug}`);
    if (stored === 'granted') {
      // Re-verify from backend
      fetch(`${BACKEND_URL}/api/proposals/${slug}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: sessionStorage.getItem(`proposal_code_${slug}`) })
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setTemplate(data); })
        .finally(() => setLoading(false));
    }
    fetch(`${BACKEND_URL}/api/proposals/${slug}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(setMeta)
      .catch(() => setMeta(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/proposals/${slug}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      if (res.ok) {
        const data = await res.json();
        setTemplate(data);
        sessionStorage.setItem(`proposal_${slug}`, 'granted');
        sessionStorage.setItem(`proposal_code_${slug}`, code);
      } else {
        setError('Invalid access code');
      }
    } catch { setError('Failed to verify'); }
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: requestName, email: requestEmail, proposal: slug })
      });
      setRequestSent(true);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!meta && !template) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white text-center px-4">
        <div>
          <h1 className="text-2xl font-bold mb-2">Proposal Not Found</h1>
          <p className="text-slate-400">This proposal doesn't exist or has been removed.</p>
          <a href="/" className="mt-4 inline-block text-blue-400 hover:text-blue-300 text-sm">Back to Portfolio</a>
        </div>
      </div>
    );
  }

  // Gate — show code entry
  if (!template) {
    const color = meta.primary_color || '#3b82f6';
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: meta.accent_color || '#0f172a' }}>
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `${color}20` }}>
              <Lock style={{ color }} size={24} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">{meta.company_name}</h1>
            {meta.tagline && <p className="text-slate-400 text-sm">{meta.tagline}</p>}
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-4">
            <h2 className="text-white font-semibold text-center mb-4">This Proposal is Private</h2>
            <form onSubmit={handleCodeSubmit} className="space-y-3">
              <input
                type="text"
                value={code}
                onChange={(e) => { setCode(e.target.value); setError(''); }}
                placeholder="Enter access code"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 text-center tracking-widest"
                data-testid="proposal-code-input"
              />
              {error && <p className="text-red-400 text-xs text-center">{error}</p>}
              <button type="submit" className="w-full py-3 rounded-xl font-semibold text-white transition-colors" style={{ background: color }} data-testid="proposal-code-submit">
                View Proposal
              </button>
            </form>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <h3 className="text-white text-sm font-semibold text-center mb-3">Request Access</h3>
            {requestSent ? (
              <p className="text-slate-400 text-sm text-center">Request sent! Check your email.</p>
            ) : (
              <form onSubmit={handleRequestAccess} className="space-y-3">
                <input type="text" value={requestName} onChange={(e) => setRequestName(e.target.value)} placeholder="Your name" required className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm" data-testid="proposal-request-name" />
                <input type="email" value={requestEmail} onChange={(e) => setRequestEmail(e.target.value)} placeholder="Your email" required className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm" data-testid="proposal-request-email" />
                <button type="submit" className="w-full py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg transition-colors" data-testid="proposal-request-submit">
                  Request Access Code
                </button>
              </form>
            )}
          </div>

          <div className="text-center mt-4">
            <a href="/" className="text-slate-500 hover:text-slate-300 text-sm">Back to Portfolio</a>
          </div>
        </div>
      </div>
    );
  }

  // Render full proposal
  return (
    <div className="min-h-screen bg-white" data-testid="dynamic-proposal">
      {template.sections && template.sections.map((section, i) => {
        const Renderer = SECTION_RENDERERS[section.type] || CustomSection;
        return <Renderer key={i} section={section} template={template} />;
      })}

      {/* Footer */}
      <footer className="py-8 text-center" style={{ background: template.accent_color }}>
        <p className="text-slate-400 text-sm">
          &copy; {new Date().getFullYear()} Sierra Scala |{' '}
          <a href="/" className="text-blue-400 hover:text-blue-300">View Full Portfolio</a>
        </p>
      </footer>
    </div>
  );
};

export default DynamicProposalPage;
